import { computed, ref } from 'vue';
import Evaporate from 'evaporate';
import useIcarusMultipartConfig from '@/composables/useIcarusMultipartConfig.js';

/**
 * Upload file directly to s3
 */

export function useFileUpload() {
  const uploadedFileLocation = ref(null);
  const uploadSuccess = ref(false);
  const uploadErrors = ref(null);
  const uploadProgress = ref(0);

  const samples = [];                      // [{t: ms, f: 0-1}]
  const WINDOW_MS = 5000;                  // 5-sec smoothing window

  const progressInPercentage = computed(() => {
    return `${uploadProgress.value}%`;
  });

  const formatEta = (s) => {
    if (s >= 3600) {
      const h = Math.round(s / 3600);
      return `${h} hour${h !== 1 ? 's' : ''}`;
    }
    if (s >= 60) {
      const m = Math.round(s / 60);
      return `${m} minute${m !== 1 ? 's' : ''}`;
    }
    const sec = Math.max(1, Math.round(s));
    return `${sec} second${sec !== 1 ? 's' : ''}`;
  };

  const timeRemainingText = computed(() => {
    if (uploadProgress.value === 0 || uploadProgress.value === 100) return null;
    const now = Date.now();
    const curFraction = uploadProgress.value / 100;

    /* keep only recent samples */
    samples.push({ t: now, f: curFraction });
    while (samples[0] && now - samples[0].t > WINDOW_MS) samples.shift();

    if (samples.length < 2) return null;

    const dt = (samples[samples.length - 1].t - samples[0].t) / 1000;  // seconds
    const df = samples[samples.length - 1].f - samples[0].f;            // 0-1

    const speed = df / dt;                     // fraction per second
    if (speed <= 0) return null;

    const remainingSec = (1 - curFraction) / speed;
    return formatEta(remainingSec);
  });

  const uploadFile = async (file) => {
    const {
      multipartConfigSuccess,
      timeUrl,
      signerUrl,
      awsKey,
      awsSignatureVersion,
      bucket,
      logging,
      maxConcurrentParts,
      progressIntervalMS,
      partSize,
      maxRetryBackoffSecs,
      retryBackoffPower
    } = await useIcarusMultipartConfig();

    if (multipartConfigSuccess) {
      const config = {
        signerUrl: signerUrl.value,
        timeUrl: timeUrl.value,
        aws_key: awsKey.value,
        awsSignatureVersion: awsSignatureVersion.value,
        bucket: bucket.value,
        logging: logging.value,
        maxConcurrentParts: maxConcurrentParts.value,
        progressIntervalMS: progressIntervalMS.value,
        partSize: partSize.value,
        maxRetryBackoffSecs: maxRetryBackoffSecs.value,
        retryBackoffPower: retryBackoffPower.value
      };

      // set custom location file name
      const locationFileName = `partnrup/evaporate/${Math.floor(1000000000 * Math.random())}/${file.name.replace(/[^a-zA-Z0-9_\.]+/g, '_')}`;

      return Evaporate.create(config).then(async (evaporate) => {
        const evaporateConfig = {
          name: locationFileName,
          file: file,
          xAmzHeadersAtInitiate: {
            'x-amz-acl': 'public-read'
          },
          contentType: file.type,
          complete: function (_xhr, awsObjectKey, stats) {
            console.log('Media File Upload Complete................', stats);
            uploadSuccess.value = true;
            uploadedFileLocation.value = awsObjectKey;
          },
          cancelled: function () {
            console.log('upload cancelled!');
          },
          info: function (r) {
            console.log('info', r);
          },
          warn: function (r) {
            console.log('warn', r);
          },
          error: function (r) {
            console.log('ERROR!', r);
          },
          progress: (fraction) => {
            uploadProgress.value = Math.round(fraction * 100);
          }
        };

        await evaporate.add(evaporateConfig).then(
          (awsObjectKey) => {
            // console.log('Media File Upload Complete................', awsObjectKey);
          },
          (reason) => {
            console.log('File did not upload successfully:', reason);
          }
        );
      });
    }
  };

  return {
    uploadFile,
    uploadProgress,
    progressInPercentage,
    timeRemainingText,
    uploadedFileLocation,
    uploadSuccess,
    uploadErrors
  };
}
